import { ENV } from '../../config/runtimeEnv';
import { ActivePlanStageOverview, TreatmentPlanSectionInfo } from "../../types/healthcare/TreatmentPlanTypes";
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

// ── Response types ────────────────────────────────────────────────────────────

export interface DashboardAppointmentType {
	id: number;
	name: string;
	color: string | null;
	duration_minutes: number | null;
}

export interface DashboardAppointmentSummary {
	id: number;
	patient_id: UserUuid;
	patient_name: string | null;
	practitioner_id?: UserUuid | null;
	practitioner_name?: string | null;
	practitioner_color?: string | null;
	title: string;
	start_time: string;
	end_time: string;
	status: string;
	location: string | null;
	category: string | null;
	appointment_type: DashboardAppointmentType | null;
}

export interface DashboardCriticalTestResult {
	id: number;
	patient_id: UserUuid;
	test_category: string;
	priority: string;
	status: string;
	is_abnormal: boolean;
	sample_id: string | null;
	testing_provider: { id: number; name: string } | null;
	created_at: string;
}

export interface DashboardActivePlan {
	id: number;
	patient_id: UserUuid;
	patient_name: string | null;
	status: string;
	assigned_at: string;
	template: { id: number; name: string; category: string } | null;
	current_stage: { id: number; stage_type: string; title: string } | null;
	stages: ActivePlanStageOverview[];
	sections: TreatmentPlanSectionInfo[];
}

export interface DashboardNewestPatient {
	id: UserUuid;
	uuid: string;
	first_name: string;
	last_name: string;
	email: string | null;
	profile_picture: string | null;
	created_at: string;
}

export interface DashboardPendingForm {
	id: number;
	patient_id: UserUuid;
	form_id: number;
	status: string;
	created_at: string;
	form: { id: number; title: string; form_type: string | null } | null;
}

export interface PractitionerDashboardResponse {
	generated_at: string;
	date_range: { start: string; end: string };
	appointments: {
		stats: {
			total: number;
			today_count: number;
			upcoming: number;
			cancelled: number;
		};
		today_schedule: DashboardAppointmentSummary[];
		by_category: Record<string, number>;
	};
	patients: {
		total: number;
		active: number;
		inactive: number;
		new_this_period: number;
	};
	test_orders: {
		stats: {
			total: number;
			pending: number;
			results_available: number;
			completed: number;
			stat_pending: number;
		};
		critical_results: DashboardCriticalTestResult[];
	};
	treatment_plans: {
		stats: {
			total: number;
			active: number;
			paused: number;
			completed: number;
		};
		active_plans: DashboardActivePlan[];
	};
	forms: {
		stats: {
			total: number;
			pending: number;
			in_progress: number;
			completed: number;
		};
		pending_forms: DashboardPendingForm[];
	};
	newest_patients: DashboardNewestPatient[];
	alerts: {
		critical_count: number;
		requires_attention: boolean;
	};
}

export interface PractitionerDashboardParams {
	start_date?: string;
	end_date?: string;
}

// ── API function ──────────────────────────────────────────────────────────────

export const fetchPractitionerDashboard = async (
	params: PractitionerDashboardParams
): Promise<PractitionerDashboardResponse> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/dashboard/summary`, {
		params,
	});
	return response.data;
};
